import { useStores } from '@/store/hooks/useStore';
import ThreadTemplateAction from '../deep-mining/task-template/thread-template-action';
import UserMessageGroup from '../message/user-message-group';
import { triggerTemplateDetail } from '../deep-mining/template/template-detail-modal';
import AssistantMessageGroup from '../message/assistant-message-group';
import AssistantMessageContainer from '../message/assistant-message-container';
import AssistantMessage from '../message/assistant-message';
import ThreadLoading from './thread-loading';
import {
  checkIsHideMessage,
  getStreamingRenderToolCalls,
  isInlineLikeTool,
} from '@/lib/thread';
import { useMemo } from 'react';
import RatingCard from '../deep-mining/rating-card';
import { useI18n } from '@/hooks/use-i18n';
import ThreadInterruptMessage from '../message/thread-interrupt-message';
import { cn } from '@/lib/utils';
// NOTE: 不再用 content 匹配，改用 message_id 锚定
import { usePendingMessage } from '@/hooks/use-thread-deferred-send';

interface ThreadMessageListProps {
  groupedMessages: MESSAGE.Group[];
  handleSubmitMessage: THREAD.SendMessageFn;

  // TODO 临时用 props 先传进来
  enableCreateTemplate?: boolean;
  agentStatus?: string;
  threadAuditStatus?: AUDIT.ThreadAuditStatus;
  feedbackByMessageId?: Record<string, THREAD.Feedback[]>;
  batchInsertFeedbacks?: (feedbacks: THREAD.Feedback[]) => void;
  batchDeleteFeedbacks?: (feedbackIds: string[]) => void;
  isRunning?: boolean;
  rateAgentRunId?: string;
  setRateAgentRunId?: (id: string) => void;
  scrollToBottom?: () => void;
  isStopping?: boolean;
  onStopAgent?: () => void;
}

const ThreadMessageList: React.FC<ThreadMessageListProps> = ({
  groupedMessages,
  handleSubmitMessage,

  // 临时的
  enableCreateTemplate,
  agentStatus,
  threadAuditStatus,
  feedbackByMessageId,
  batchInsertFeedbacks,
  batchDeleteFeedbacks,
  isRunning,
  rateAgentRunId,
  setRateAgentRunId,
  scrollToBottom,
  isStopping,
  onStopAgent,
}) => {
  const { t } = useI18n('agent');
  const stores = useStores();
  const { permissions } = stores.userStore;
  const {
    threadInfo,
    threadStatus,
    threadStatusInfo,
    topMessageIds,
    messageMap,
    streamingTextContent,
  } = stores.threadStore;
  const { streamingToolCalls, toolMap } = stores.threadToolStore;
  const { sandboxStatus } = stores.threadProjectStore;
  const {
    templateMap,
    generate: generateTemplate,
    regenerate: regenerateTemplate,
    del: delTemplate,
    view: viewTemplate,
    close: closeTemplate,
  } = stores.threadTemplateStore;

  const [templatePermission, feedbackPermission, ratePermission] =
    useMemo(() => {
      const templatePermission = permissions?.includes('template');
      const feedbackPermission = permissions?.includes('feedback');
      const ratePermission = permissions?.includes('thread:agent_run:rating');
      return [templatePermission, feedbackPermission, ratePermission];
    }, [permissions]);
  const threadTemplateActions = {
    generateTemplate,
    delTemplate,
    regenerateTemplate,
    viewTemplate,
    closeTemplate,
  };
  const hasStreamingContent = streamingToolCalls || streamingTextContent;
  const lastMessage = messageMap[topMessageIds[topMessageIds.length - 1]];
  const lastIsShowUserMessage =
    lastMessage?.type === 'user' && !checkIsHideMessage(lastMessage);
  const isShowThreadLoading = useMemo(() => {
    return (
      (agentStatus === 'running' || agentStatus === 'connecting') &&
      (topMessageIds.length === 0 || lastIsShowUserMessage) &&
      !hasStreamingContent
    );
  }, [
    agentStatus,
    topMessageIds.length,
    lastIsShowUserMessage,
    hasStreamingContent,
  ]);

  const { showBottomPending, PendingUserMessage } = usePendingMessage({
    groupedMessages,
    isStopping: Boolean(isStopping),
    handleSubmitMessage,
    onStopAgent,
  });

  return (
    <div className="[&>*:not(:first-child)]:mt-4">
      {groupedMessages.map((group, groupIndex) => {
        const isLastMessage = groupIndex === groupedMessages?.length - 1;
        const isUserGroup = group.type === 'user_group';
        const isAssistantGroup = group.type === 'assistant_group';
        const tamplateUserMsgId = isAssistantGroup
          ? group.userMsgKey
          : undefined;
        const template = templateMap[tamplateUserMsgId];

        const processThreadAction = templatePermission && !isLastMessage && (
          <ThreadTemplateAction
            scene="process"
            data={template}
            className="mt-2 mb-2"
            {...threadTemplateActions}
          />
        );

        const lastThreadAction = templatePermission && isLastMessage && (
          <ThreadTemplateAction
            scene="last"
            data={template}
            className="mt-6"
            enableCreateTemplate={enableCreateTemplate}
            agentStatus={agentStatus}
            threadAuditStatus={threadAuditStatus}
            threadStopStatus={threadStatus}
            templateUserMsgId={tamplateUserMsgId}
            {...threadTemplateActions}
          />
        );

        if (isUserGroup) {
          return (
            <UserMessageGroup
              key={group.key}
              group={group}
              handleTemplateClick={(templateId) => {
                triggerTemplateDetail({ token: templateId });
              }}
            />
          );
        } else if (group.type === 'assistant_group') {
          const isLastAssistantGroup =
            groupIndex === groupedMessages?.length - 1;
          return (
            <div
              key={group.key}
              className={cn(
                'w-[100%]',
                group.preGroupMessageIsHide ? '!mt-0' : '',
              )}
            >
              <div className="inline-flex w-[100%] rounded-lg bg-muted/5 px-0 text-sm">
                <div className="space-y-2 w-[100%]">
                  <AssistantMessageGroup
                    groupData={group}
                    extraElm={processThreadAction}
                    enableFeedback={feedbackPermission}
                    threadId={threadInfo?.id}
                    feedbackByMessageId={feedbackByMessageId}
                    batchInsertFeedbacks={batchInsertFeedbacks}
                    batchDeleteFeedbacks={batchDeleteFeedbacks}
                  />

                  {/* 正在流式返回的消息和 loading 图标 */}
                  {isLastAssistantGroup && isRunning && (
                    <AssistantMessageContainer isFirst={false}>
                      <AssistantMessage
                        data={{
                          content: streamingTextContent,
                          tool_calls: getStreamingRenderToolCalls(
                            streamingToolCalls,
                            toolMap,
                          ) as any,
                        }}
                      />

                      {/* 这个三元表示正处于二次发送消息的暂停阶段中，这个时候只展示最下面的Loading动画，不展示上面的流式返回的消息和 loading 图标*/}
                      {stores.threadStore.pendingMessages.length > 0 ? (
                        <></>
                      ) : (
                        <ThreadLoading />
                      )}
                    </AssistantMessageContainer>
                  )}

                  {/* 评分 */}
                  {ratePermission &&
                    agentStatus === 'idle' &&
                    isLastAssistantGroup &&
                    rateAgentRunId &&
                    threadStatus === 'FULL_COMPLETED' &&
                    group?.messages?.find((m) => {
                      if (m.type !== 'assistant') {
                        return false;
                      }
                      const parsedContent = m?.content;
                      return parsedContent?.tool_calls?.some(
                        (_) => !isInlineLikeTool(_.name),
                      );
                    }) && (
                      <div>
                        <RatingCard
                          agentRunId={rateAgentRunId}
                          onClose={() => {
                            console.log('RatingCard onClose');
                            setRateAgentRunId(null);
                          }}
                          scrollToBottom={scrollToBottom}
                        />
                      </div>
                    )}

                  {lastThreadAction}
                </div>
              </div>
            </div>
          );
        }
        return null;
      })}
      {/* 部分需要处理的异常中止，在组件中分别处理 */}
      {threadStatus && (
        <ThreadInterruptMessage
          status={threadStatus}
          statusInfo={threadStatusInfo}
          handleSubmitMessage={handleSubmitMessage}
          isStopping={isStopping}
        />
      )}

      {/* 假的用户消息 - 
          在 暂停中 到 addAgent 接口返回内容前这段时间，展示假的用户消息
      */}
      {showBottomPending && <PendingUserMessage />}

      {/* 三个点的 loading 动画
      只要是正在运行，或者有 pending 消息，就展示 loading 动画
      */}
      {(isShowThreadLoading ||
        stores.threadStore.pendingMessages.length > 0) && (
        <ThreadLoading
          text={
            sandboxStatus === 'CREATING'
              ? t('chat.startingService')
              : sandboxStatus === 'RUNNING' && (agentStatus as any) !== 'idle'
                ? t('chat.thinking')
                : ''
          }
        />
      )}
    </div>
  );
};

export default ThreadMessageList;
